import { NodeTypes } from "src/enums/NodeTypes";
import { workflowStepIterator } from "src/services/workflowStepIterator";
import { applicationStore } from "src/store/ApplicationStore";
import { toast } from "react-toastify";
import { socketInprogressServiceStatus } from "src/enums/socketInprogressServiceStatus";

/**
 * Button configurations by node type
 * To add buttons for a new node type, add a new entry to this object
 * 
 * @example
 * [NodeTypes.NEW_TYPE]: {
 *   buttons: [
 *     {
 *       translationKey: 'actions.editForm',
 *       requiresEditableForm: true,
 *       action: { retry: true, edit_form: true, edit_form_for: NodeTypes.NEW_TYPE }
 *     },
 *     {
 *       translationKey: 'actions.retryButton',
 *       action: { retry: true, target_node: NodeTypes.NEW_TYPE }
 *     }
 *   ]
 * }
 */
export const NODE_BUTTON_CONFIG = {
    [NodeTypes.VERIFICATION]: {
        buttons: [
            {
                translationKey: 'actions.editForm',
                requiresEditableForm: true,
                action: { retry: true, edit_form: true, edit_form_for: NodeTypes.VERIFICATION }
            },
            {
                translationKey: 'actions.reUploadId',
                action: { retry: true }
            }
        ]
    },
    [NodeTypes.ADDRESS_VERIFICATION]: {
        buttons: [
            {
                translationKey: 'actions.editForm',
                requiresEditableForm: true,
                action: { retry: true, edit_form: true, edit_form_for: NodeTypes.ADDRESS_VERIFICATION }
            },
            {
                translationKey: 'actions.reUploadPoa',
                action: { retry: true }
            }
        ]
    },
    [NodeTypes.LIVENESS]: {
        buttons: [
            {
                translationKey: 'actions.reUploadId',
                action: { retry: true, target_node: NodeTypes.LIVENESS, retry_node_for_target_node: NodeTypes.VERIFICATION }
            },
            {
                translationKey: 'actions.redoSelfieAndLiveness',
                action: { retry: true }
            }
        ]
    },
    [NodeTypes.SELFIE]: {
        buttons: [
            {
                translationKey: 'actions.retryThis',
                action: { retry: true }
            }
        ]
    },
    [NodeTypes.GEOLOCATION_VERIFICATON]: {
        buttons: [
            {
                translationKey: 'actions.editUserInfo',
                requiresNoAddressVerificationOrFailed: true,
                action: { retry: true, edit_form: true, edit_form_for: NodeTypes.GEOLOCATION_VERIFICATON }
            },
            {
                translationKey: 'actions.retryThis',
                action: { retry: true }
            }
        ]
    },
    [NodeTypes.PAYMENT_VERIFICATION]: {
        buttons: [
            {
                translationKey: 'actions.retryThis',
                action: { retry: true, target_node: NodeTypes.PAYMENT_VERIFICATION }
            }
        ]
    },
};

/**
 * Creates button click handler and calls workflowStepIterator
 * 
 * @param {Object} action - Action object to be sent to workflow step iterator
 * @param {Function} navigate - React Router navigate function
 * @param {Function} t - i18n translation function
 * @param {string} nodeId - Node ID from inprogress_services
 */
export const createButtonHandler = async (action, navigate, t, nodeId) => {
    try {
        const contextOfStep = {
            ...action,
            ...(action && !action.edit_form && nodeId ? { node_id: nodeId } : { node_id_for_form_update: nodeId }),
            ...(action?.edit_form && { country: applicationStore?.country.toUpperCase() })
        };

        const requestPayload = {
            access_token: applicationStore.access_token,
            constextOfStep: contextOfStep
        };

        const response = await workflowStepIterator(requestPayload);
        applicationStore.setWorkflowNextServiceResponse(response.data);
        applicationStore.setLastContextOfStep(contextOfStep);
        navigate("/next", { replace: true });
    } catch (error) {
        toast.error(error?.response?.data?.detail ? error?.response?.data?.detail : t('errors.generic'));
    }
};

/**
 * Creates buttons based on node type
 * Returns default retry button if not defined in config
 * 
 * @param {string} nodeName - Node type (e.g., NodeTypes.VERIFICATION)
 * @param {Function} navigate - React Router navigate function
 * @param {Function} t - i18n translation function
 * @param {string} nodeId - Node ID from inprogress_services
 * @param {Object} nodeSettings - Node settings from node_settings_for_player
 * @returns {Array} Array of button objects
 */
export const getButtonsForNode = (nodeName, navigate, t, nodeId, nodeSettings = null) => {
    const config = NODE_BUTTON_CONFIG[nodeName];
    if (!config) {
        return [{
            label: t('actions.retryThis'),
            onClick: () => createButtonHandler({ retry: true, target_node: nodeName }, navigate, t, nodeId)
        }];
    }
    const isOverrideData = nodeSettings?.overrideData === true;
    const isVerificationDisabled = applicationStore.workflowNextServiceResponse?.verification === false;

    const inprogressServices = applicationStore.workflowNextServiceResponse?.inprogress_services || [];
    const addressVerificationService = inprogressServices.find(
        service => service.microservice_id === NodeTypes.ADDRESS_VERIFICATION
    );
    const shouldShowEditUserInfo = !addressVerificationService ||
        addressVerificationService?.status === socketInprogressServiceStatus.FAILED;

    return config.buttons
        .filter(buttonConfig => {
            if (buttonConfig.requiresEditableForm) {
                if (isOverrideData) {
                    return false;
                }
                if (!applicationStore.workflowNextServiceResponse?.is_user_info_editable) {
                    return false;
                }
            }
            if (buttonConfig.requiresNoAddressVerificationOrFailed) {
                if (!shouldShowEditUserInfo) {
                    return false;
                }
            }
            if (nodeName === NodeTypes.LIVENESS && isVerificationDisabled && buttonConfig.translationKey === 'actions.reUploadId') {
                return false;
            }
            return true;
        })
        .map(buttonConfig => ({
            label: t(buttonConfig.translationKey),
            onClick: () => createButtonHandler(buttonConfig.action, navigate, t, nodeId)
        }));
};

